import { type ElementType, useState, useEffect } from 'react';
import { Buildings, User, Users, Lock, CheckCircle, ArrowRight, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useNavigate } from '../lib/routing';
import { getDirectionStatus, completeOnboarding, type FrLegalStatus } from '../lib/direction';

const STATUTS: Array<{ value: FrLegalStatus; label: string }> = [
  { value: 'public', label: 'Public' },
  { value: 'prive_contrat', label: "Privé sous contrat d'association" },
  { value: 'prive_simple', label: 'Privé sous contrat simple' },
  { value: 'hors_contrat', label: 'Hors contrat' },
];

type School = {
  name: string;
  address: string;
  fr_legal_status: FrLegalStatus | '';
  school_year: string;
  student_count: string;
};

type Profile = { first_name: string; last_name: string; poste: string };
type SecondHolder = { first_name: string; last_name: string; email: string };

export default function DirectorOnboarding() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  const [rgpdAccepted, setRgpdAccepted] = useState(false);
  const [school, setSchool] = useState<School>({ name: '', address: '', fr_legal_status: '', school_year: '', student_count: '' });
  const [profile, setProfile] = useState<Profile>({ first_name: '', last_name: '', poste: '' });
  const [secondHolder, setSecondHolder] = useState<SecondHolder>({ first_name: '', last_name: '', email: '' });
  const [skipSecondHolder, setSkipSecondHolder] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDirectionStatus().then((s) => {
      if (cancelled) return;
      if (!s || !s.needs_onboarding) navigate('/direction');
      else setChecking(false);
    }).catch(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [navigate]);

  const schoolComplete = !!school.name.trim() && !!school.fr_legal_status;
  const profileComplete = !!profile.first_name.trim() && !!profile.last_name.trim();
  const secondHolderComplete = skipSecondHolder
    || (!!secondHolder.first_name.trim() && !!secondHolder.last_name.trim() && secondHolder.email.includes('@'));
  const canSubmit = rgpdAccepted && schoolComplete && profileComplete && secondHolderComplete;

  const handleConfirm = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await completeOnboarding({
        school: {
          name: school.name.trim(),
          address: school.address.trim() || null,
          fr_legal_status: school.fr_legal_status as FrLegalStatus,
          school_year: school.school_year.trim() || null,
          student_count: school.student_count ? parseInt(school.student_count, 10) : null,
        },
        profile: {
          first_name: profile.first_name.trim(),
          last_name: profile.last_name.trim(),
          poste: profile.poste.trim() || null,
        },
        second_holder: skipSecondHolder ? null : {
          first_name: secondHolder.first_name.trim(),
          last_name: secondHolder.last_name.trim(),
          email: secondHolder.email.trim(),
        },
      });
      navigate('/direction');
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) return <></>;

  return (
    <div className="min-h-dvh bg-background">

      <header className="sticky top-0 z-10 bg-card border-b border-border shadow-card">
        <div className="max-w-[640px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center shrink-0">
              <span className="text-primary-foreground text-[13px] font-black leading-none">n</span>
            </div>
            <span className="font-extrabold text-[15px] text-foreground">naia edu</span>
          </div>
          <span className="text-[11px] font-bold text-primary bg-accent px-2.5 py-1 rounded-full tracking-wide uppercase">
            Première connexion
          </span>
        </div>
      </header>

      <main className="max-w-[640px] mx-auto px-5 pt-8 pb-16">

        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={18} weight="fill" className="text-primary shrink-0" />
            <span className="text-[12px] font-bold text-primary uppercase tracking-widest">
              Bienvenue, directeur·rice
            </span>
          </div>
          <h1 className="text-[28px] font-black text-foreground leading-tight m-0">
            Configurons votre espace
          </h1>
          <p className="text-[15px] text-muted-foreground leading-relaxed mt-2 m-0 max-w-[480px]">
            Remplissez les sections ci-dessous. Chaque bloc se déverrouille à mesure que vous avancez.
          </p>
        </div>

        <div className="flex flex-col gap-4">

          <RgpdBlock accepted={rgpdAccepted} onChange={setRgpdAccepted} />

          <RevealSection show={rgpdAccepted}>
            <SchoolBlock school={school} onChange={setSchool} />
          </RevealSection>

          <RevealSection show={rgpdAccepted && schoolComplete}>
            <ProfileBlock profile={profile} onChange={setProfile} />
          </RevealSection>

          <RevealSection show={rgpdAccepted && schoolComplete && profileComplete}>
            <SecondHolderBlock
              person={secondHolder}
              onChange={setSecondHolder}
              skip={skipSecondHolder}
              onSkipChange={setSkipSecondHolder}
            />
          </RevealSection>

          <RevealSection show={canSubmit}>
            <div className="pt-2 pb-4">
              <Button
                className="w-full h-12 text-[15px] font-extrabold rounded-xl gap-2"
                onClick={() => setShowModal(true)}
              >
                Vérifier et créer mon espace
                <ArrowRight size={16} weight="bold" />
              </Button>
            </div>
          </RevealSection>

        </div>
      </main>

      {showModal && (
        <RecapModal
          school={school}
          profile={profile}
          secondHolder={skipSecondHolder ? null : secondHolder}
          submitting={submitting}
          onClose={() => setShowModal(false)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}

function RevealSection({ show, children }: { show: boolean; children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, [show]);

  if (!show) return null;
  return (
    <div className={cn(
      'transition-all duration-500 ease-out',
      visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
    )}>
      {children}
    </div>
  );
}

function SectionCard({ icon: Icon, title, subtitle, completed, children }: {
  icon: ElementType;
  title: string;
  subtitle: string;
  completed?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card border border-border rounded-card shadow-card overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-accent/60">
        <div className={cn(
          'w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border transition-all duration-300',
          completed ? 'bg-primary border-primary' : 'bg-accent border-border',
        )}>
          {completed
            ? <CheckCircle size={18} weight="fill" className="text-primary-foreground" />
            : <Icon size={18} weight="duotone" className="text-primary" />}
        </div>
        <div>
          <h2 className="text-[15px] font-extrabold text-foreground m-0">{title}</h2>
          <p className="text-[12px] text-muted-foreground mt-0.5 m-0">{subtitle}</p>
        </div>
      </div>
      <div className="px-5 py-5">{children}</div>
    </div>
  );
}

function RgpdBlock({ accepted, onChange }: { accepted: boolean; onChange: (v: boolean) => void }) {
  return (
    <SectionCard icon={Lock} title="Consentement RGPD" subtitle="Requis avant toute création de données" completed={accepted}>
      <p className="text-[13.5px] text-muted-foreground leading-relaxed mb-4">
        En validant ce formulaire, votre établissement reste responsable de traitement.
        Naia intervient en qualité de sous-traitant conformément au RGPD.
      </p>
      <button
        type="button"
        onClick={() => onChange(!accepted)}
        className={cn(
          'flex items-start gap-3 w-full px-4 py-3.5 rounded-xl border-2 transition-all cursor-pointer text-left',
          accepted ? 'border-primary bg-accent' : 'border-border bg-card hover:border-primary/40',
        )}
      >
        <div className={cn(
          'w-5 h-5 rounded mt-[1px] flex items-center justify-center shrink-0 border-2 transition-all',
          accepted ? 'bg-primary border-primary' : 'border-border',
        )}>
          {accepted && <Check size={11} weight="bold" className="text-primary-foreground" />}
        </div>
        <span className="text-[13.5px] font-medium text-foreground leading-snug">
          J'ai lu et j'accepte les{' '}
          <span className="text-primary font-semibold underline underline-offset-2">conditions d'utilisation</span>
          {' '}et la politique de traitement des données personnelles.
        </span>
      </button>
      <p className="text-[12px] text-muted-foreground mt-2">
        Consulter les{' '}
        <a href="/cgu" target="_blank" rel="noreferrer" className="text-naia-blue underline">conditions d'utilisation</a>
        {' '}et la{' '}
        <a href="/confidentialite" target="_blank" rel="noreferrer" className="text-naia-blue underline">politique de confidentialité</a>.
      </p>
    </SectionCard>
  );
}

function SchoolBlock({ school, onChange }: { school: School; onChange: (s: School) => void }) {
  const set = (key: keyof School) => (val: string) => onChange({ ...school, [key]: val });
  const completed = !!school.name.trim() && !!school.fr_legal_status;
  return (
    <SectionCard icon={Buildings} title="Votre établissement" subtitle="Informations officielles" completed={completed}>
      <div className="flex flex-col gap-4">
        <Field label="Nom de l'établissement *" value={school.name} onChange={set('name')} placeholder="Ex : Collège Jean Moulin" />
        <Field label="Adresse postale" value={school.address} onChange={set('address')} placeholder="Ex : 12 rue de la République, 69001 Lyon" />
        <div className="flex flex-col gap-1.5">
          <label className="text-[12.5px] font-semibold text-foreground">Statut juridique *</label>
          <NativeSelect
            value={school.fr_legal_status}
            onChange={(e) => set('fr_legal_status')(e.target.value)}
            className="h-10 text-[14px]"
          >
            <option value="">Choisir…</option>
            {STATUTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </NativeSelect>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Année scolaire" value={school.school_year} onChange={set('school_year')} placeholder="2026-2027" />
          <Field label="Effectif élèves" value={school.student_count} onChange={set('student_count')} placeholder="Ex : 450" type="number" />
        </div>
      </div>
    </SectionCard>
  );
}

function ProfileBlock({ profile, onChange }: { profile: Profile; onChange: (p: Profile) => void }) {
  const set = (key: keyof Profile) => (val: string) => onChange({ ...profile, [key]: val });
  const completed = !!profile.first_name.trim() && !!profile.last_name.trim();
  return (
    <SectionCard icon={User} title="Votre profil" subtitle="Directeur·rice général·e" completed={completed}>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prénom *" value={profile.first_name} onChange={set('first_name')} placeholder="Sophie" />
          <Field label="Nom *" value={profile.last_name} onChange={set('last_name')} placeholder="Martin" />
        </div>
        <Field label="Intitulé de poste" value={profile.poste} onChange={set('poste')} placeholder="Ex : Principale de collège" />
      </div>
    </SectionCard>
  );
}

function SecondHolderBlock({
  person, onChange, skip, onSkipChange,
}: { person: SecondHolder; onChange: (p: SecondHolder) => void; skip: boolean; onSkipChange: (v: boolean) => void }) {
  const set = (key: keyof SecondHolder) => (val: string) => onChange({ ...person, [key]: val });
  const completed = skip || (!!person.first_name.trim() && !!person.last_name.trim() && person.email.includes('@'));
  return (
    <SectionCard icon={Users} title="Second titulaire" subtitle="Co-gestion de l'espace direction" completed={completed}>
      <div className="flex flex-col gap-4">
        {!skip && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prénom" value={person.first_name} onChange={set('first_name')} placeholder="Marc" />
              <Field label="Nom" value={person.last_name} onChange={set('last_name')} placeholder="Bernard" />
            </div>
            <Field label="Email professionnel" value={person.email} onChange={set('email')} placeholder="adjoint@etablissement.fr" type="email" />
          </>
        )}
        <button
          type="button"
          onClick={() => onSkipChange(!skip)}
          className={cn(
            'flex items-center gap-2.5 w-full px-4 py-3 rounded-xl border transition-all cursor-pointer text-left',
            skip ? 'border-primary bg-accent' : 'border-border bg-card hover:border-primary/40',
          )}
        >
          <div className={cn(
            'w-4 h-4 rounded flex items-center justify-center shrink-0 border-2 transition-all',
            skip ? 'bg-primary border-primary' : 'border-border',
          )}>
            {skip && <Check size={9} weight="bold" className="text-primary-foreground" />}
          </div>
          <span className="text-[13px] text-muted-foreground">Je le ferai plus tard</span>
        </button>
      </div>
    </SectionCard>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[12.5px] font-semibold text-foreground">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 text-[14px]"
        min={type === 'number' ? 1 : undefined}
      />
    </div>
  );
}

function RecapModal({ school, profile, secondHolder, submitting, onClose, onConfirm }: {
  school: School;
  profile: Profile;
  secondHolder: SecondHolder | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const statutLabel = STATUTS.find((s) => s.value === school.fr_legal_status)?.label ?? '';
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !submitting) onClose(); }}>
      <DialogContent showCloseButton={!submitting} style={{ maxWidth: 560, maxHeight: '85dvh' }}>
        <DialogHeader>
          <DialogTitle>Vérification</DialogTitle>
          <p className="text-[12px] text-muted-foreground m-0">Relisez avant de créer votre espace</p>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 px-5 py-5 flex flex-col gap-5">
          <RecapSection title="Établissement">
            <RecapRow label="Nom" value={school.name} />
            <RecapRow label="Adresse" value={school.address} />
            <RecapRow label="Statut" value={statutLabel} />
            <RecapRow label="Année scolaire" value={school.school_year} />
            <RecapRow label="Effectif élèves" value={school.student_count} />
          </RecapSection>

          <RecapSection title="Directeur·rice général·e">
            <RecapRow label="Nom" value={[profile.first_name, profile.last_name].filter(Boolean).join(' ')} />
            <RecapRow label="Poste" value={profile.poste} />
          </RecapSection>

          {secondHolder ? (
            <RecapSection title="Second titulaire">
              <RecapRow label="Nom" value={[secondHolder.first_name, secondHolder.last_name].filter(Boolean).join(' ')} />
              <RecapRow label="Email" value={secondHolder.email} />
            </RecapSection>
          ) : (
            <RecapSection title="Second titulaire">
              <RecapRow label="" value="À renseigner plus tard" />
            </RecapSection>
          )}
        </div>

        <div className="px-5 py-4 border-t border-border shrink-0">
          <Button
            className="w-full h-12 text-[15px] font-extrabold rounded-xl gap-2"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? 'Création en cours…' : 'Créer mon espace'}
            {!submitting && <ArrowRight size={16} weight="bold" />}
          </Button>
          {!submitting && (
            <button
              type="button"
              onClick={onClose}
              className="w-full mt-2 h-9 text-[13px] text-muted-foreground font-medium hover:text-foreground transition-colors cursor-pointer"
            >
              Modifier
            </button>
          )}
        </div>

      </DialogContent>
    </Dialog>
  );
}

function RecapSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-[11px] font-black text-primary uppercase tracking-[.1em] mb-2 m-0">{title}</h3>
      <div className="bg-accent/50 rounded-xl overflow-hidden divide-y divide-border">
        {children}
      </div>
    </div>
  );
}

function RecapRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-4 py-2.5">
      <span className="text-[12px] text-muted-foreground shrink-0">{label}</span>
      <span className="text-[13px] font-semibold text-foreground text-right">{value || '—'}</span>
    </div>
  );
}
